import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Building2, LogOut, Save, Loader2, Upload, Globe, Phone, Mail, Wifi, Umbrella, Snowflake, MapPin } from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';
import AnnouncementsManager from '@/components/AnnouncementsManager';
import ProductsManager from '@/components/ProductsManager';
import LanguageToggle from '@/components/LanguageToggle';

export default function ExhibitorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [companyForm, setCompanyForm] = useState({});
  const [boothForm, setBoothForm] = useState({});

  const notify = useCallback((msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 2500);
  }, []);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/exhibitor/me`, {
        headers: { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' },
      });
      setMe(res.data.data);
      const c = res.data.data.company || {};
      setCompanyForm({
        description: c.description || '',
        website: c.website || '',
        phone: c.phone || '',
        email: c.email || '',
        whatsapp: c.whatsapp || '',
        country: c.country || '',
        address: c.address || '',
        logo: c.logo || '',
      });
      const b = res.data.data.booth || {};
      setBoothForm({
        has_wifi: !!b.has_wifi,
        has_shelter: !!b.has_shelter,
        has_ac: !!b.has_ac,
        description: b.description || '',
      });
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/login');
      } else if (e.response?.status === 404) {
        setMe({ error: e.response.data?.message || 'No booth linked' });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authApi.isAuthenticated()) { navigate('/login'); return; }
    fetchMe();
  }, [fetchMe, navigate]);

  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { notify('Logo too large — max 500KB', true); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCompanyForm((prev) => ({ ...prev, logo: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/exhibitor/company`, companyForm, {
        headers: { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      notify(t('exhibitor.saved'));
    } catch (e) {
      notify(e.response?.data?.message || e.response?.data?.detail || t('exhibitor.save_failed'), true);
    } finally {
      setSaving(false);
    }
  };

  const saveBooth = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/exhibitor/booth`, boothForm, {
        headers: { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      notify(t('exhibitor.saved'));
    } catch (e) {
      notify(e.response?.data?.message || e.response?.data?.detail || t('exhibitor.save_failed'), true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;
  }

  if (me?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-slate-900/70 rounded-2xl p-8 border border-slate-800">
          <Building2 className="w-12 h-12 mx-auto text-slate-500 mb-4" />
          <h2 className="text-white font-bold text-xl mb-2">{t('exhibitor.no_booth')}</h2>
          <p className="text-slate-400 text-sm mb-6">{me.error}</p>
          <button onClick={handleLogout} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold">
            {t('nav.logout')}
          </button>
        </div>
      </div>
    );
  }

  const c = me?.company || {};
  const b = me?.booth;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950" data-testid="exhibitor-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">{t('exhibitor.title')}</div>
            <div className="text-white font-bold truncate">{c.name}</div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-white text-sm font-semibold">{me?.user?.name}</div>
            <div className="text-slate-400 text-xs">{me?.user?.email}</div>
          </div>
          <LanguageToggle />
          <button onClick={handleLogout} className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 text-xs font-bold flex items-center gap-1.5" data-testid="exhibitor-logout-btn">
            <LogOut className="w-3.5 h-3.5" /> {t('nav.logout')}
          </button>
        </div>
      </header>

      {flash && (
        <div className={`fixed top-24 right-6 z-50 px-4 py-2 rounded-lg shadow-2xl text-sm font-semibold ${flash.isError ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`} data-testid="exhibitor-flash">
          {flash.msg}
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        {/* Booth summary */}
        <aside className="space-y-4">
          <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-800">
            <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold mb-2">{t('exhibitor.your_booth')}</div>
            <div className="text-white font-black text-2xl font-mono">{b?.booth_number || 'N/A'}</div>
            <div className="text-slate-400 text-sm mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {b?.hall?.name || t('exhibitor.no_booth')}</div>
            {b?.category && <div className="mt-2 text-xs text-slate-300">{b.category.icon} {b.category.name}</div>}
            <div className="text-slate-500 text-xs mt-3">{t('exhibitor.position')}: X={b?.position_x} · Z={b?.position_z}</div>
          </div>

          {/* Amenities */}
          <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-800">
            <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold mb-3">{t('exhibitor.amenities')}</div>
            <div className="space-y-2">
              {[
                { key: 'has_wifi', icon: Wifi, label: t('exhibitor.wifi') },
                { key: 'has_shelter', icon: Umbrella, label: t('exhibitor.shelter') },
                { key: 'has_ac', icon: Snowflake, label: t('exhibitor.ac') },
              ].map(({ key, icon: Icon, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!boothForm[key]}
                    onChange={(e) => setBoothForm((p) => ({ ...p, [key]: e.target.checked }))}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid={`amenity-${key}`}
                  />
                  <Icon className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-200 text-sm">{label}</span>
                </label>
              ))}
            </div>
            <button
              onClick={saveBooth}
              disabled={saving}
              className="w-full mt-4 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="save-amenities-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {t('exhibitor.save_amenities')}
            </button>
          </div>
        </aside>

        {/* Company edit form */}
        <section className="lg:col-span-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-800">
          <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold mb-4">{t('exhibitor.company_profile')}</div>

          {/* Logo */}
          <div className="mb-6 flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
              {companyForm.logo ? (
                <img src={companyForm.logo} alt="logo" className="w-full h-full object-cover" data-testid="company-logo-preview" />
              ) : (
                <Building2 className="w-8 h-8 text-slate-600" />
              )}
            </div>
            <div className="flex-1">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer" data-testid="upload-logo-btn">
                <Upload className="w-3.5 h-3.5" /> {t('exhibitor.upload_logo')}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
              </label>
              <div className="text-slate-500 text-[10px] mt-1">{t('exhibitor.logo_hint')}</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t('exhibitor.website')} icon={Globe}>
              <input type="url" value={companyForm.website} onChange={(e) => setCompanyForm((p) => ({ ...p, website: e.target.value }))} placeholder="https://example.gov.my" className={inputCls} data-testid="input-website" />
            </Field>
            <Field label={t('exhibitor.public_email')} icon={Mail}>
              <input type="email" value={companyForm.email} onChange={(e) => setCompanyForm((p) => ({ ...p, email: e.target.value }))} placeholder="info@..." className={inputCls} data-testid="input-email" />
            </Field>
            <Field label={t('exhibitor.phone')} icon={Phone}>
              <input value={companyForm.phone} onChange={(e) => setCompanyForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+60..." className={inputCls} data-testid="input-phone" />
            </Field>
            <Field label={t('exhibitor.whatsapp')} icon={Phone}>
              <input value={companyForm.whatsapp} onChange={(e) => setCompanyForm((p) => ({ ...p, whatsapp: e.target.value }))} placeholder="60123456789" className={inputCls} data-testid="input-whatsapp" />
            </Field>
            <Field label={t('exhibitor.country')}>
              <input value={companyForm.country} onChange={(e) => setCompanyForm((p) => ({ ...p, country: e.target.value }))} className={inputCls} data-testid="input-country" />
            </Field>
            <Field label={t('exhibitor.address')}>
              <input value={companyForm.address} onChange={(e) => setCompanyForm((p) => ({ ...p, address: e.target.value }))} className={inputCls} data-testid="input-address" />
            </Field>
          </div>

          <div className="mt-4">
            <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mb-1 block">{t('exhibitor.company_description')}</label>
            <textarea
              value={companyForm.description}
              onChange={(e) => setCompanyForm((p) => ({ ...p, description: e.target.value }))}
              rows={6}
              placeholder=""
              className={inputCls}
              data-testid="input-description"
            />
          </div>

          <button
            onClick={saveCompany}
            disabled={saving}
            className="mt-5 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="save-company-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('exhibitor.save_profile')}
          </button>

          {/* Announcements & Coupons */}
          <AnnouncementsManager boothNumber={b?.booth_number} />

          {/* Products / Services */}
          <ProductsManager boothNumber={b?.booth_number} />

          {/* Coupon Redemption CTA */}
          <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 flex items-center gap-3" data-testid="redeem-cta">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 grid place-items-center">
              <svg className="w-5 h-5 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2"/><path d="M22 15v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2"/><path d="M12 5v14"/></svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm">Redeem Visitor Coupons</div>
              <div className="text-cyan-200 text-xs">Enter a code or scan a QR to verify + mark as redeemed.</div>
            </div>
            <button onClick={() => navigate('/exhibitor/redeem')} className="px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-xs font-black uppercase tracking-widest" data-testid="open-redeem-page">Open</button>
          </div>
        </section>
      </main>
    </div>
  );
}

const inputCls = 'w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-white text-sm outline-none';

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mb-1 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      {children}
    </div>
  );
}
